import { getTranslation } from 'helpers/getTranslation';

import {
  isAuthMethodContextEvent,
  isAuthSourceContextEvent,
  resolveAuthMethodFromProperties,
  translateAuthMethod,
  translateAuthMethodVia,
} from './analyticsPropertyLabels';
import {
  formatEventContext,
  getSessionEndTitle,
  getSlowScreenTitle,
} from './formatEventContext';
import {
  eventNameToTranslationKey,
  getAnalyticsEventBaseName,
  getAnalyticsEventContextSuffix,
  JOURNEY_END_LABEL,
  ROOT_EVENT_NAME,
} from './analyticsEventUtils';
import {
  getApiErrorTitle,
  getClientErrorTitle,
  getRageClickTitle,
  getSlowRequestTitle,
} from './journeyEndpointLabels';
import {
  getLessonTypeIconName,
  hasLessonNameInProperties,
  isLessonRelatedEventKey,
  resolveLessonTypeFromProperties,
} from './lessonTypeIcons';
import {
  getModuleTypeIconName,
  isModuleContextEventKey,
} from './moduleTypeIcons';
import {
  resolveCatalogContext,
  resolveCatalogParentLabel,
  type CatalogContextDisplay,
} from './catalogContextDisplay';

export {
  eventNameToTranslationKey,
  getAnalyticsEventBaseName,
  getAnalyticsEventContextSuffix,
} from './analyticsEventUtils';

const EVENTS_PREFIX = 'student_page.timeline.events';

/**
 * Shown instead of an untranslated (new or renamed) event name. Lives outside
 * `events.*` so no event name can map onto it.
 */
const UNKNOWN_EVENT_KEY = 'student_page.timeline.unknown_event';

const TASK_NUMBERED_EVENT_KEYS = new Set([
  'plugin_intro_task_started',
  'plugin_intro_task_completed',
  'task_started',
  'task_completed',
  'task_skipped',
]);

const readPositiveNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;

const readNonEmptyString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const parseTaskNumbersFromSuffix = (
  suffix: string | null,
): { taskNumber: number | null; taskTotal: number | null; context: string | null } => {
  if (!suffix) {
    return { taskNumber: null, taskTotal: null, context: null };
  }

  const taskOnlyMatch = suffix.match(/^(\d+)(?:\/(\d+))?$/);

  if (taskOnlyMatch) {
    return {
      taskNumber: Number(taskOnlyMatch[1]),
      taskTotal: taskOnlyMatch[2] ? Number(taskOnlyMatch[2]) : null,
      context: null,
    };
  }

  const taskTailMatch = suffix.match(/ · (\d+)(?:\/(\d+))?$/);

  if (taskTailMatch) {
    return {
      taskNumber: Number(taskTailMatch[1]),
      taskTotal: taskTailMatch[2] ? Number(taskTailMatch[2]) : null,
      context: suffix.slice(0, suffix.lastIndexOf(' · ')).trim() || null,
    };
  }

  return { taskNumber: null, taskTotal: null, context: suffix };
};

const resolveTaskNumbers = (
  eventName: string,
  properties?: Record<string, unknown>,
) => {
  const contextSuffix = getAnalyticsEventContextSuffix(eventName);
  const fromSuffix = parseTaskNumbersFromSuffix(contextSuffix);

  const taskNumber =
    readPositiveNumber(properties?.task_number) ?? fromSuffix.taskNumber;
  const taskTotal =
    readPositiveNumber(properties?.task_total ?? properties?.tasks_total) ??
    fromSuffix.taskTotal;

  return {
    taskNumber,
    taskTotal,
    context: fromSuffix.context,
  };
};

const hasTranslation = (key: string) => getTranslation(key) !== key;

/** True when the event name has its own copy (otherwise the title is «Действие в приложении»). */
export const isKnownAnalyticsEvent = (eventName: string): boolean => {
  const baseName = getAnalyticsEventBaseName(eventName);

  if (baseName === ROOT_EVENT_NAME || baseName === JOURNEY_END_LABEL) {
    return true;
  }

  const key = `${EVENTS_PREFIX}.${eventNameToTranslationKey(baseName)}`;

  return hasTranslation(key) || hasTranslation(`${key}_generic`);
};

/** Generic title for events without copy; the raw name only when even that is missing. */
const translateUnknownEvent = (baseName: string) => {
  const fallback = getTranslation(UNKNOWN_EVENT_KEY);

  return fallback === UNKNOWN_EVENT_KEY ? baseName : fallback;
};

export const translateAnalyticsEvent = (
  eventName: string,
  options?: {
    taskNumber?: number | null;
    taskTotal?: number | null;
  },
): string => {
  const baseName = getAnalyticsEventBaseName(eventName);

  if (baseName === ROOT_EVENT_NAME) {
    return getTranslation(`${EVENTS_PREFIX}.session_start`);
  }

  if (baseName === JOURNEY_END_LABEL) {
    return getTranslation(`${EVENTS_PREFIX}.path_ended`);
  }

  const translationKey = eventNameToTranslationKey(baseName);
  const key = `${EVENTS_PREFIX}.${translationKey}`;

  if (TASK_NUMBERED_EVENT_KEYS.has(translationKey)) {
    if (options?.taskNumber != null && Number.isFinite(options.taskNumber)) {
      const numberedKey =
        options.taskTotal != null ? `${key}_with_total` : key;
      const numberedTranslation = getTranslation(numberedKey, {
        number: options.taskNumber,
        total: options.taskTotal ?? options.taskNumber,
      });

      if (numberedTranslation !== numberedKey) {
        return numberedTranslation;
      }
    }

    const genericKey = `${key}_generic`;
    const genericTranslation = getTranslation(genericKey);

    if (genericTranslation !== genericKey) {
      return genericTranslation;
    }
  }

  const translated = getTranslation(key);

  if (!translated.includes('{{') || !translated.includes('}}')) {
    return translated === key ? translateUnknownEvent(baseName) : translated;
  }

  const genericKey = `${key}_generic`;
  const genericTranslation = getTranslation(genericKey);

  if (genericTranslation !== genericKey) {
    return genericTranslation;
  }

  return translateUnknownEvent(baseName);
};

export type AnalyticsEventDisplay = {
  title: string;
  subtitle: string | null;
  moduleLabel: string | null;
  moduleTypeIcon: string | null;
  lessonLabel: string | null;
  lessonTypeIcon: string | null;
  catalogContext: CatalogContextDisplay | null;
  baseName: string;
  /**
   * Raw values for the «Технические детали» mode only: request, status, error name,
   * selector, unresolved ids, untranslated event name. Never shown by default.
   */
  technicalDetails: string | null;
};

export type FormatAnalyticsEventDisplayOptions = {
  studentEmail?: string | null;
  /** `SessionEvent.platform`: «Закрыл плагин» / «Закрыл вкладку», «Сбой в плагине» / «Сбой на сайте». */
  platform?: string | null;
  /** The row repeats its screen's lesson / module: no catalog block and no names in the subtitle. */
  omitCatalog?: boolean;
};

const resolveLessonLabel = (
  properties: Record<string, unknown> | undefined,
  contextFromSuffix: string | null,
): string | null =>
  readNonEmptyString(properties?.lesson_name) ?? contextFromSuffix;

const shouldShowModuleLabel = (
  translationKey: string,
  properties: Record<string, unknown> | undefined,
): boolean =>
  isModuleContextEventKey(translationKey) ||
  isLessonRelatedEventKey(translationKey) ||
  Boolean(resolveCatalogParentLabel(properties));

const parseModuleNameFromContextSuffix = (
  moduleContextSuffix: string,
  properties?: Record<string, unknown>,
): string | null => {
  const lessonName = readNonEmptyString(properties?.lesson_name);

  if (lessonName && moduleContextSuffix.endsWith(` · ${lessonName}`)) {
    const modulePart = moduleContextSuffix.slice(0, -(lessonName.length + 3)).trim();

    return modulePart.length > 0 ? modulePart : null;
  }

  const separatorIndex = moduleContextSuffix.indexOf(' · ');

  if (separatorIndex === -1) {
    return moduleContextSuffix;
  }

  return moduleContextSuffix.slice(0, separatorIndex).trim() || null;
};

const resolveModuleLabel = (
  translationKey: string,
  properties: Record<string, unknown> | undefined,
  moduleContextSuffix: string | null,
): string | null => {
  const fromProperties = resolveCatalogParentLabel(properties);

  if (fromProperties) {
    return fromProperties;
  }

  if (isModuleContextEventKey(translationKey) && moduleContextSuffix) {
    return parseModuleNameFromContextSuffix(moduleContextSuffix, properties);
  }

  return null;
};

const shouldShowLessonLabel = (
  translationKey: string,
  properties: Record<string, unknown> | undefined,
): boolean =>
  isLessonRelatedEventKey(translationKey) ||
  (isModuleContextEventKey(translationKey) && hasLessonNameInProperties(properties));

const stripDisplayedLabelsFromSuffix = (
  suffix: string | null,
  labels: Array<string | null | undefined>,
): string | null => {
  if (!suffix) {
    return null;
  }

  let remaining = suffix;

  labels.forEach((label) => {
    if (!label) {
      return;
    }

    if (remaining === label) {
      remaining = '';
      return;
    }

    if (remaining.startsWith(`${label} · `)) {
      remaining = remaining.slice(label.length + 3).trim();
      return;
    }

    if (remaining.endsWith(` · ${label}`)) {
      remaining = remaining.slice(0, -(label.length + 3)).trim();
    }
  });

  return remaining.length > 0 ? remaining : null;
};

const resolveModuleDisplay = (
  translationKey: string,
  properties: Record<string, unknown> | undefined,
  moduleContextSuffix: string | null,
): Pick<AnalyticsEventDisplay, 'moduleLabel' | 'moduleTypeIcon'> => {
  if (!shouldShowModuleLabel(translationKey, properties) && !moduleContextSuffix) {
    return { moduleLabel: null, moduleTypeIcon: null };
  }

  const moduleLabel = resolveModuleLabel(translationKey, properties, moduleContextSuffix);

  if (!moduleLabel) {
    return { moduleLabel: null, moduleTypeIcon: null };
  }

  return {
    moduleLabel,
    moduleTypeIcon: getModuleTypeIconName(properties),
  };
};

const buildContextSubtitle = ({
  baseName,
  properties,
  translationKey,
  eventName,
  lessonDisplay,
  moduleDisplay,
  explicitContextSuffix,
  displayOptions,
}: {
  baseName: string;
  properties?: Record<string, unknown>;
  translationKey: string;
  eventName: string;
  lessonDisplay: Pick<AnalyticsEventDisplay, 'lessonLabel' | 'lessonTypeIcon'>;
  moduleDisplay: Pick<AnalyticsEventDisplay, 'moduleLabel' | 'moduleTypeIcon'>;
  explicitContextSuffix?: string | null;
  displayOptions?: FormatAnalyticsEventDisplayOptions;
}): string | null => {
  const hasLessonLabel = Boolean(lessonDisplay.lessonLabel);
  const hasModuleLabel = Boolean(moduleDisplay.moduleLabel);
  let contextSuffix =
    explicitContextSuffix !== undefined
      ? explicitContextSuffix
      : getAnalyticsEventContextSuffix(eventName);

  if (isAuthMethodContextEvent(baseName)) {
    const authMethod =
      resolveAuthMethodFromProperties(properties) ?? contextSuffix;

    if (authMethod && (!contextSuffix || contextSuffix === authMethod)) {
      contextSuffix = null;
    }
  }

  if (isAuthSourceContextEvent(baseName)) {
    /* The source (legacy label suffix) is re-read from properties and translated. */
    contextSuffix = null;
  }

  if (hasModuleLabel && contextSuffix === moduleDisplay.moduleLabel) {
    contextSuffix = null;
  }

  if (hasModuleLabel && isModuleContextEventKey(translationKey)) {
    contextSuffix = null;
  }

  if (hasLessonLabel && contextSuffix === lessonDisplay.lessonLabel) {
    contextSuffix = null;
  }

  contextSuffix = stripDisplayedLabelsFromSuffix(contextSuffix, [
    moduleDisplay.moduleLabel,
    lessonDisplay.lessonLabel,
  ]);

  if (hasModuleLabel || hasLessonLabel || displayOptions?.omitCatalog) {
    contextSuffix = null;
  }

  return mergeContextSubtitle(
    contextSuffix,
    formatEventContext(baseName, properties, {
      omitLessonName: hasLessonLabel,
      omitModuleName: hasModuleLabel,
      omitCatalog: displayOptions?.omitCatalog,
      studentEmail: displayOptions?.studentEmail,
    }),
  );
};

const MAX_TECHNICAL_MESSAGE_LENGTH = 140;

const truncateText = (value: string, maxLength: number) =>
  value.length <= maxLength
    ? value
    : `${value.slice(0, maxLength - 1).trimEnd()}…`;

/**
 * Developer-only line: what the default view deliberately hides. A row that
 * repeats its chapter's lesson / module (`omitCatalog`) leaves out the
 * unresolved ids: the chapter header already shows them once.
 */
const buildTechnicalDetails = (
  translationKey: string,
  baseName: string,
  properties: Record<string, unknown> | undefined,
  omitCatalog = false,
): string | null => {
  const read = (key: string) => readNonEmptyString(properties?.[key]);
  const status = properties?.status;
  const request = read('endpoint')
    ? [read('method'), read('endpoint')].filter(Boolean).join(' ')
    : null;
  const message = read('message');

  return joinSubtitleParts(
    isKnownAnalyticsEvent(baseName) ? null : baseName,
    request,
    typeof status === 'number' ? String(status) : read('error_code'),
    read('error_kind'),
    read('error_name'),
    message ? truncateText(message, MAX_TECHNICAL_MESSAGE_LENGTH) : null,
    read('message_type'),
    translationKey === 'rage_click' ? read('target') : null,
    read('stage'),
    !omitCatalog && read('lesson_id') && !read('lesson_name')
      ? `lesson_id ${read('lesson_id')}`
      : null,
    !omitCatalog && read('module_id') && !resolveCatalogParentLabel(properties)
      ? `module_id ${read('module_id')}`
      : null,
  );
};

/** «Ответил верно» / «Ответил неверно»; «Ответил на задачу» when the client did not grade it. */
const getTaskAnswerTitle = (
  translationKey: string,
  properties: Record<string, unknown> | undefined,
): string => {
  if (properties?.is_correct === true || properties?.is_correct === false) {
    return getTranslation(
      `${EVENTS_PREFIX}.task_answered_${
        properties.is_correct ? 'correct' : 'incorrect'
      }`,
    );
  }

  return getTranslation(`${EVENTS_PREFIX}.${translationKey}`);
};

/** Diagnostic / exit / answer events whose title depends on their properties. */
const PROPERTY_TITLE_BUILDERS: Record<
  string,
  (
    properties: Record<string, unknown> | undefined,
    options?: FormatAnalyticsEventDisplayOptions,
  ) => string
> = {
  api_error: (properties) => getApiErrorTitle(properties),
  slow_request: (properties) => getSlowRequestTitle(properties),
  slow_screen: (properties) => getSlowScreenTitle(properties),
  rage_click: (properties) => getRageClickTitle(properties),
  client_error: (properties, options) =>
    getClientErrorTitle(properties, options?.platform),
  session_ended: (properties, options) =>
    getSessionEndTitle(properties, options?.platform),
  task_answered: (properties) =>
    getTaskAnswerTitle('task_answered', properties),
  lesson_task_answered: (properties) =>
    getTaskAnswerTitle('lesson_task_answered', properties),
};

const assembleEventDisplay = ({
  title,
  baseName,
  translationKey,
  eventName,
  properties,
  lessonContextSuffix,
  moduleContextSuffix = null,
  fixedSubtitle,
  displayOptions,
}: {
  title: string;
  baseName: string;
  translationKey: string;
  eventName: string;
  properties?: Record<string, unknown>;
  lessonContextSuffix?: string | null;
  moduleContextSuffix?: string | null;
  fixedSubtitle?: string | null;
  displayOptions?: FormatAnalyticsEventDisplayOptions;
}): AnalyticsEventDisplay => {
  const suffixFromEventName = getAnalyticsEventContextSuffix(eventName);
  const moduleSuffix =
    moduleContextSuffix ??
    (isModuleContextEventKey(translationKey) ? suffixFromEventName : null);
  const lessonDisplay = resolveLessonDisplay(
    translationKey,
    properties,
    lessonContextSuffix ?? null,
  );
  const moduleDisplay = resolveModuleDisplay(translationKey, properties, moduleSuffix);
  const catalogContext = displayOptions?.omitCatalog
    ? null
    : resolveCatalogContext({
        translationKey,
        properties,
        moduleLabel: moduleDisplay.moduleLabel,
        lessonLabel: lessonDisplay.lessonLabel,
        moduleTypeIcon: moduleDisplay.moduleTypeIcon,
        lessonTypeIcon: lessonDisplay.lessonTypeIcon,
      });

  return {
    title,
    subtitle:
      fixedSubtitle ??
      buildContextSubtitle({
        baseName,
        properties,
        translationKey,
        eventName,
        lessonDisplay,
        moduleDisplay,
        displayOptions,
        ...(lessonContextSuffix !== undefined ? { explicitContextSuffix: lessonContextSuffix } : {}),
      }),
    ...moduleDisplay,
    ...lessonDisplay,
    catalogContext,
    baseName,
    technicalDetails: buildTechnicalDetails(
      translationKey,
      baseName,
      properties,
      displayOptions?.omitCatalog,
    ),
  };
};

const resolveLessonDisplay = (
  translationKey: string,
  properties: Record<string, unknown> | undefined,
  contextFromSuffix: string | null,
): Pick<AnalyticsEventDisplay, 'lessonLabel' | 'lessonTypeIcon'> => {
  if (!shouldShowLessonLabel(translationKey, properties)) {
    return { lessonLabel: null, lessonTypeIcon: null };
  }

  const lessonLabel = resolveLessonLabel(properties, contextFromSuffix);

  if (!lessonLabel) {
    return { lessonLabel: null, lessonTypeIcon: null };
  }

  return {
    lessonLabel,
    lessonTypeIcon: getLessonTypeIconName(resolveLessonTypeFromProperties(properties)),
  };
};

const joinSubtitleParts = (...parts: Array<string | null | undefined>): string | null => {
  const filtered = parts.filter((part): part is string => Boolean(part && part.length > 0));

  return filtered.length > 0 ? filtered.join(' · ') : null;
};

const mergeContextSubtitle = (
  contextSuffix: string | null,
  eventContext: string | null,
): string | null => {
  if (!contextSuffix) {
    return eventContext;
  }

  if (!eventContext) {
    return contextSuffix;
  }

  if (contextSuffix === eventContext || eventContext.includes(contextSuffix)) {
    return eventContext;
  }

  return joinSubtitleParts(contextSuffix, eventContext);
};

/** Title for events that name the sign-in method: «Вошёл через Google», «Выбрал вход по почте». */
const resolveAuthMethodTitle = (
  baseName: string,
  translationKey: string,
  authMethod: string | null,
): string => {
  const key = `${EVENTS_PREFIX}.${translationKey}`;
  const method = authMethod ? translateAuthMethod(authMethod) : null;
  const methodVia = authMethod ? translateAuthMethodVia(authMethod) : null;

  if (method && methodVia) {
    return getTranslation(key, { method, method_via: methodVia });
  }

  const genericKey = `${key}_generic`;
  const genericTitle = getTranslation(genericKey);

  return genericTitle !== genericKey
    ? genericTitle
    : translateAnalyticsEvent(baseName);
};

/** Localized title + human subtitle + catalog block for one journey event. */
export const formatAnalyticsEventDisplay = (
  eventName: string,
  properties?: Record<string, unknown>,
  displayOptions?: FormatAnalyticsEventDisplayOptions,
): AnalyticsEventDisplay => {
  const baseName = getAnalyticsEventBaseName(eventName);
  const translationKey = eventNameToTranslationKey(baseName);
  const { taskNumber, taskTotal, context } = resolveTaskNumbers(eventName, properties);

  if (translationKey === 'activation_completed') {
    const suffixContext = getAnalyticsEventContextSuffix(eventName);
    const parsedSuffix = parseTaskNumbersFromSuffix(suffixContext);
    const activationTaskNumber =
      readPositiveNumber(properties?.task_number) ?? parsedSuffix.taskNumber;

    return assembleEventDisplay({
      title: getTranslation(`${EVENTS_PREFIX}.activation_completed`),
      baseName,
      translationKey,
      eventName,
      properties,
      lessonContextSuffix: parsedSuffix.context ?? context,
      fixedSubtitle:
        activationTaskNumber != null
          ? getTranslation('student_page.timeline.context.activation_task', {
              number: activationTaskNumber,
            })
          : null,
      displayOptions,
    });
  }

  const propertyTitleBuilder = PROPERTY_TITLE_BUILDERS[translationKey];

  if (propertyTitleBuilder) {
    return assembleEventDisplay({
      title: propertyTitleBuilder(properties, displayOptions),
      baseName,
      translationKey,
      eventName,
      properties,
      displayOptions,
    });
  }

  if (
    TASK_NUMBERED_EVENT_KEYS.has(translationKey) &&
    taskNumber != null &&
    Number.isFinite(taskNumber)
  ) {
    return assembleEventDisplay({
      title: translateAnalyticsEvent(eventName, { taskNumber, taskTotal }),
      baseName,
      translationKey,
      eventName,
      properties,
      lessonContextSuffix: context,
      displayOptions,
    });
  }

  if (isAuthMethodContextEvent(baseName)) {
    const authMethod =
      resolveAuthMethodFromProperties(properties) ??
      getAnalyticsEventContextSuffix(eventName);

    return assembleEventDisplay({
      title: resolveAuthMethodTitle(baseName, translationKey, authMethod),
      baseName,
      translationKey,
      eventName,
      properties,
      displayOptions,
    });
  }

  if (isAuthSourceContextEvent(baseName)) {
    const legacySource = getAnalyticsEventContextSuffix(eventName);
    const authProperties =
      legacySource && !readNonEmptyString(properties?.source)
        ? { ...properties, source: legacySource }
        : properties;

    return assembleEventDisplay({
      title: translateAnalyticsEvent(eventName),
      baseName,
      translationKey,
      eventName,
      properties: authProperties,
      displayOptions,
    });
  }

  return assembleEventDisplay({
    title: translateAnalyticsEvent(eventName),
    baseName,
    translationKey,
    eventName,
    properties,
    displayOptions,
  });
};
